import { CHAT_REACTIONS } from './limits'
import {
	quoteOfMessage,
	sameTag,
	type ChatAuthor,
	type ChatMessage,
	type ChatQuote,
	type ChatReaction,
	type ChatReactionChoice,
	type ChatReactionCount,
	type ChatTag,
	type OutgoingMessage,
} from './types'

export type SendStatus = 'sending' | 'failed'

/**
 * A Message I sent that the server hasn't confirmed. It is keyed by the client id the
 * send carries, which the server echoes back on its copy.
 */
export interface PendingMessage {
	clientId: string
	message: OutgoingMessage
	/** When I sent it, in Unix seconds. */
	date: number
	status: SendStatus
}

/**
 * Where the "N new" divider goes: before the first Message from someone else after
 * `afterMessageId` (any, when null). `count` is the server's unread count, so it stays
 * right while the first unread Message is still on an unloaded page.
 */
export interface UnreadMarker {
	afterMessageId: number | null
	count: number
}

/** A change of my reaction on its way to the server. */
export interface ReactionChange {
	messageId: number
	sequence: number
}

/**
 * The reaction I want on a Message while changes to it are on their way, over the copy
 * the server last sent, which a failure goes back to.
 */
interface ReactionInFlight {
	latest: number
	wanted: ChatReactionChoice | null
	settled: ChatMessage
}

/**
 * What a Chat screen holds: the Messages loaded so far, oldest first, and the ones I've
 * sent that the server hasn't confirmed yet. Pure state, so the screen's behaviour tests
 * without a server; ported from iOS `ChatStream`. Every change returns a new stream.
 */
export interface ChatStream {
	/** Oldest first. */
	readonly messages: readonly ChatMessage[]
	/** The `before` cursor for the next, older page; null once the oldest is loaded. */
	readonly olderCursor: number | null
	/** In the order I sent them; they show after every confirmed Message. */
	readonly pending: readonly PendingMessage[]
	/** Fixed when the Chat opens, so reading what's on screen doesn't move it. */
	readonly unreadMarker: UnreadMarker | null
	/** The Messages whose link Add as idea is on its way to the server for. */
	readonly addingIdeas: readonly number[]
	readonly reactionsInFlight: Readonly<Record<number, ReactionInFlight>>
	readonly reactionSequence: number
	/**
	 * Messages I deleted while the server hasn't said so, each with the copy it last sent,
	 * which a failure puts back.
	 */
	readonly deletionsInFlight: Readonly<Record<number, ChatMessage>>
	/** The newest id any page has brought, which says whether a refetch is contiguous. */
	readonly newestPagedId: number | null
}

export const emptyStream = (): ChatStream => ({
	messages: [],
	olderCursor: null,
	pending: [],
	unreadMarker: null,
	addingIdeas: [],
	reactionsInFlight: {},
	reactionSequence: 0,
	deletionsInFlight: {},
	newestPagedId: null,
})

export const hasOlder = (stream: ChatStream): boolean => stream.olderCursor !== null

// MARK: - Pages

/**
 * Takes the newest page, as the server sends it: newest first. Called on opening and
 * again on every live event. Older pages already loaded stay, unless this page doesn't
 * reach back to them: then whatever came in between is unknown, so the stream starts
 * over from this page rather than keep a silent gap.
 */
export const applyNewestPage = (stream: ChatStream, page: readonly ChatMessage[], nextBefore: number | null): ChatStream => {
	const ids = page.map(message => message.id)
	// Measured against the pages, not my own confirmed sends: a send's id is newer than
	// any gap the socket may have missed before it.
	const reachesHeld =
		nextBefore === null ||
		stream.newestPagedId === null ||
		(ids.length > 0 ? Math.min(...ids) : Infinity) <= stream.newestPagedId
	let next = reachesHeld ? stream : { ...stream, messages: [] }
	if (!reachesHeld || next.messages.length === 0) next = { ...next, olderCursor: nextBefore }
	const newest = Math.max(next.newestPagedId ?? -Infinity, ...ids)
	next = { ...next, newestPagedId: Number.isFinite(newest) ? newest : null }
	return merge(next, page)
}

/**
 * Takes the page older than `requestedBefore`. A page asked for under a cursor the
 * stream has since dropped, because it started over, is ignored.
 */
export const applyOlderPage = (
	stream: ChatStream,
	page: readonly ChatMessage[],
	nextBefore: number | null,
	requestedBefore: number,
): ChatStream => {
	if (requestedBefore !== stream.olderCursor) return stream
	return { ...merge(stream, page), olderCursor: nextBefore }
}

const oldestHeldId = (stream: ChatStream): number => stream.messages[0]?.id ?? Infinity

/**
 * Whether the first Message I haven't read is on a page not loaded yet, so the divider
 * can't sit in its place. Not before I've read anything: then the whole history is
 * unread, and the divider opens what's loaded.
 */
export const needsOlderPageForUnread = (stream: ChatStream): boolean => {
	const afterMessageId = stream.unreadMarker?.afterMessageId
	if (!hasOlder(stream) || afterMessageId == null) return false
	// Ids are shared by every trip's Chat, so only reaching the read Message itself
	// proves nothing unread is older than what's loaded.
	return oldestHeldId(stream) > afterMessageId
}

/**
 * Whether `messageId` is on a page not loaded yet, so it can't be shown until that page
 * is. A Message reached from its Discussion, or a quote, can be anywhere back.
 */
export const needsOlderPage = (stream: ChatStream, messageId: number): boolean =>
	hasOlder(stream) && oldestHeldId(stream) > messageId

/** A held Message, to reply to; null once it's deleted. */
export const quoteOf = (stream: ChatStream, messageId: number): ChatQuote | null => {
	const message = stream.messages.find(m => m.id === messageId)
	return message && !message.removed ? quoteOfMessage(message) : null
}

// MARK: - Unread

/** Places the divider from where I was when the Chat opened. */
export const markUnread = (
	stream: ChatStream,
	unread: { unreadCount: number; lastReadMessageId: number | null },
): ChatStream => ({
	...stream,
	unreadMarker:
		unread.unreadCount > 0 ? { afterMessageId: unread.lastReadMessageId, count: unread.unreadCount } : null,
})

// MARK: - Sending

/** Sending also clears the divider: answering means I've caught up. */
export const sendPending = (stream: ChatStream, message: OutgoingMessage, clientId: string, date: number): ChatStream => ({
	...stream,
	unreadMarker: null,
	pending: [...stream.pending, { clientId, message, date, status: 'sending' }],
})

/** The server's copy of a Message I sent takes the pending one's place. */
export const confirmSend = (stream: ChatStream, message: ChatMessage): ChatStream => merge(stream, [message])

export const failSend = (stream: ChatStream, clientId: string): ChatStream => ({
	...stream,
	pending: stream.pending.map(p => (p.clientId === clientId ? { ...p, status: 'failed' } : p)),
})

/**
 * Puts a failed send back to sending and returns it to send again, with the same client
 * id so the server can't make it twice. Null unless the send had failed.
 */
export const retrySend = (
	stream: ChatStream,
	clientId: string,
): { stream: ChatStream; pending: PendingMessage } | null => {
	const failed = stream.pending.find(p => p.clientId === clientId && p.status === 'failed')
	if (!failed) return null
	const pending: PendingMessage = { ...failed, status: 'sending' }
	return { stream: { ...stream, pending: stream.pending.map(p => (p.clientId === clientId ? pending : p)) }, pending }
}

export const discardSend = (stream: ChatStream, clientId: string): ChatStream => ({
	...stream,
	pending: stream.pending.filter(p => p.clientId !== clientId),
})

// MARK: - Changes

/**
 * Takes the server's new copy of a Message, such as a deleted one's placeholder. The
 * replies held that quote a deleted one read "Deleted message" at once.
 */
export const replaceMessage = (stream: ChatStream, message: ChatMessage): ChatStream => {
	const next = merge(stream, [message])
	return message.removed ? blankQuotes(next, message) : next
}

// MARK: - Deleting

/**
 * Shows a Message as deleted at once: its placeholder, and the replies held that quote
 * it as quoting a deleted one. Null when it isn't held or is deleted already; otherwise
 * finish it with the server's answer.
 */
export const deleteMessage = (stream: ChatStream, messageId: number): ChatStream | null => {
	const index = stream.messages.findIndex(m => m.id === messageId)
	if (index === -1 || stream.messages[index].removed) return null
	const messages = [...stream.messages]
	messages[index] = asRemoved(messages[index])
	// A deleted Message has no reactions; a change on its way no longer matters.
	const reactionsInFlight = { ...stream.reactionsInFlight }
	delete reactionsInFlight[messageId]
	const next = {
		...stream,
		messages,
		reactionsInFlight,
		deletionsInFlight: { ...stream.deletionsInFlight, [messageId]: stream.messages[index] },
	}
	return blankQuotes(next, messages[index])
}

/**
 * Takes the server's answer to a deletion: the placeholder, or null when it failed,
 * which puts the Message and the quotes of it back as the server last sent them.
 * `failed` says whether to tell the user: not once a refetch showed it done.
 */
export const finishDeletion = (
	stream: ChatStream,
	messageId: number,
	placeholder: ChatMessage | null,
): { stream: ChatStream; failed: boolean } => {
	const settled = stream.deletionsInFlight[messageId]
	if (!settled) return { stream, failed: false }
	const deletionsInFlight = { ...stream.deletionsInFlight }
	delete deletionsInFlight[messageId]
	const next = { ...stream, deletionsInFlight }
	if (placeholder) return { stream: replaceMessage(next, placeholder), failed: false }
	const quote = quoteOfMessage(settled)
	const messages = next.messages.map(m => (m.id === messageId ? settled : m.quote?.id === messageId ? { ...m, quote } : m))
	return { stream: { ...next, messages }, failed: true }
}

/**
 * Shows who's on a booking now on every card of it held, as after I join or leave it.
 * The server sends no Message for that, so the held cards are updated in place.
 */
export const updatePeople = (stream: ChatStream, people: ChatAuthor[], tag: ChatTag): ChatStream => ({
	...stream,
	messages: stream.messages.map(message =>
		message.tags.some(card => sameTag(card, tag))
			? { ...message, tags: message.tags.map(card => (sameTag(card, tag) ? { ...card, people } : card)) }
			: message,
	),
})

// MARK: - Reactions

/** My reaction on a held Message. */
export const myReaction = (stream: ChatStream, messageId: number): ChatReaction | null =>
	stream.messages.find(m => m.id === messageId)?.reactions.find(r => r.reactedByMe)?.reaction ?? null

const reactionRank = (reaction: ChatReaction): number => {
	const index = (CHAT_REACTIONS as readonly string[]).indexOf(reaction)
	return index === -1 ? CHAT_REACTIONS.length : index
}

/**
 * The same Message with my reaction set to `reaction`, or taken off with null, as the
 * server will count it. I have at most one.
 */
export const withMyReaction = (message: ChatMessage, reaction: ChatReactionChoice | null): ChatMessage => {
	const counts: ChatReactionCount[] = message.reactions.flatMap(count => {
		if (!count.reactedByMe) return [count]
		return count.count > 1 ? [{ ...count, count: count.count - 1, reactedByMe: false }] : []
	})
	if (reaction) {
		const index = counts.findIndex(count => count.reaction === reaction)
		if (index >= 0) counts[index] = { reaction, count: counts[index].count + 1, reactedByMe: true }
		else counts.push({ reaction, count: 1, reactedByMe: true })
	}
	// In the order the six show; one this build doesn't know keeps its place after them.
	const reactions = counts
		.map((count, index) => ({ count, index }))
		.sort((a, b) => reactionRank(a.count.reaction) - reactionRank(b.count.reaction) || a.index - b.index)
		.map(({ count }) => count)
	return { ...message, reactions }
}

/**
 * Shows my reaction set to `reaction`, or taken off with null, at once. Returns the
 * change to finish with the server's answer; null when the Message isn't held or is deleted.
 */
export const react = (
	stream: ChatStream,
	reaction: ChatReactionChoice | null,
	messageId: number,
): { stream: ChatStream; change: ReactionChange } | null => {
	const index = stream.messages.findIndex(m => m.id === messageId)
	if (index === -1 || stream.messages[index].removed) return null
	const sequence = stream.reactionSequence + 1
	const settled = stream.reactionsInFlight[messageId]?.settled ?? stream.messages[index]
	const messages = [...stream.messages]
	messages[index] = withMyReaction(messages[index], reaction)
	return {
		stream: {
			...stream,
			messages,
			reactionSequence: sequence,
			reactionsInFlight: { ...stream.reactionsInFlight, [messageId]: { latest: sequence, wanted: reaction, settled } },
		},
		change: { messageId, sequence },
	}
}

/**
 * Takes the server's answer to a change, null when it failed. Only the newest change
 * settles the Message: an older one's answer is just the latest copy to show my reaction
 * over. A failure goes back to what the server last said. `failed` says whether to tell
 * the user: not when a newer change is still on its way.
 */
export const finishReaction = (
	stream: ChatStream,
	change: ReactionChange,
	answer: ChatMessage | null,
): { stream: ChatStream; failed: boolean } => {
	const inFlight = stream.reactionsInFlight[change.messageId]
	if (!inFlight) return { stream, failed: false }
	if (change.sequence !== inFlight.latest) {
		if (!answer) return { stream, failed: false }
		const withSettled = {
			...stream,
			reactionsInFlight: { ...stream.reactionsInFlight, [change.messageId]: { ...inFlight, settled: answer } },
		}
		return { stream: merge(withSettled, [answer]), failed: false }
	}
	const reactionsInFlight = { ...stream.reactionsInFlight }
	delete reactionsInFlight[change.messageId]
	return { stream: merge({ ...stream, reactionsInFlight }, [answer ?? inFlight.settled]), failed: answer === null }
}

// MARK: - Add as idea

export const startAddingIdea = (stream: ChatStream, messageId: number): ChatStream =>
	stream.addingIdeas.includes(messageId) ? stream : { ...stream, addingIdeas: [...stream.addingIdeas, messageId] }

/** The server's answer, if any, comes in through `replaceMessage`. */
export const finishAddingIdea = (stream: ChatStream, messageId: number): ChatStream => ({
	...stream,
	addingIdeas: stream.addingIdeas.filter(id => id !== messageId),
})

// MARK: - Private

/** Inserts or replaces by id, keeping the stream oldest first. */
const merge = (stream: ChatStream, page: readonly ChatMessage[]): ChatStream => {
	const byId = new Map(stream.messages.map(message => [message.id, message]))
	for (const message of page) byId.set(message.id, message)
	const messages = [...byId.values()].sort((a, b) => a.id - b.id)
	// A copy from the server goes under my reaction while a change of it is on its way.
	const reactionsInFlight = { ...stream.reactionsInFlight }
	for (const message of page) {
		const inFlight = reactionsInFlight[message.id]
		if (!inFlight) continue
		if (message.removed) {
			delete reactionsInFlight[message.id]
			continue
		}
		reactionsInFlight[message.id] = { ...inFlight, settled: message }
		const index = messages.findIndex(m => m.id === message.id)
		messages[index] = withMyReaction(message, inFlight.wanted)
	}
	// A copy from before the server took my deletion stays deleted, and so do the quotes
	// of it; one that's deleted already settles it.
	const deletionsInFlight = { ...stream.deletionsInFlight }
	for (const message of page) {
		if (!deletionsInFlight[message.id]) continue
		if (message.removed) {
			delete deletionsInFlight[message.id]
			continue
		}
		deletionsInFlight[message.id] = message
		const index = messages.findIndex(m => m.id === message.id)
		messages[index] = asRemoved(message)
	}
	// Whichever arrives first, the send's response or a live refetch, confirms it.
	const confirmed = new Set(page.map(message => message.clientId))
	const next = {
		...stream,
		messages,
		reactionsInFlight,
		deletionsInFlight,
		pending: stream.pending.filter(p => !confirmed.has(p.clientId)),
	}
	return Object.values(deletionsInFlight).reduce(blankQuotes, next)
}

/** A Message as its placeholder shows it once deleted: who wrote it and when, and nothing else. */
const asRemoved = (message: ChatMessage): ChatMessage => ({
	...message,
	text: null,
	tags: [],
	quote: null,
	photos: [],
	reactions: [],
	linkPreview: null,
	removed: true,
})

/** The replies held that quote a deleted Message read "Deleted message". */
const blankQuotes = (stream: ChatStream, deleted: ChatMessage): ChatStream => {
	const placeholder: ChatQuote = { id: deleted.id, author: deleted.author, text: null, tags: [], removed: true }
	return { ...stream, messages: stream.messages.map(m => (m.quote?.id === deleted.id ? { ...m, quote: placeholder } : m)) }
}
